import React, { useEffect, useState } from 'react';
import { Container, IconButton, Tooltip } from '@mui/material';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import { fCurrency } from '../../../../utils/formatNumber';
import { COMPANY_DATA_FOR_PDF } from '../../../../config';
import { accessVerify, capitalize, handleNumber, safeJSONParse } from '../../../../utils/common';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { PictureAsPdf } from '@material-ui/icons';
import { getAllStockReturns, updateStockReturnStatus } from '../../../../redux/slices/stockManagementRedux/stockReturnRedux';
import utcMoment from 'moment';
import Iconify from '../../../../components/Iconify';
import PdfViewerDrawer from '../../../../components/pdfViewerDrawer';

export default function ViewReturns() {
  const { themeStretch } = useSettings();
  const [returnsList, setReturnsList] = useState([]);
  const [rowSelection, setRowSelection] = useState(false);

  const { data, addData } = useSelector((state) => state?.stockReturn);
  const { selectedLocation } = useSelector((state) => state.location);
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [workflowLog, setWorkflowLog] = useState([]);

  useEffect(() => {
    dispatch(getAllStockReturns());
  }, []);

  useEffect(() => {
    setReturnsList(
      data?.map((value) => {
        return {
          date: utcMoment(value?.dateTime).format('YYYY-MM-DD'),
          time: utcMoment(value?.dateTime).format('HH:mm:ss'),
          ...value,
          supplierInfo: safeJSONParse(value?.supplierInfo),
          userInfo: safeJSONParse(value?.userInfo),
          stockDetails: safeJSONParse(value?.stockDetails),
        };
      })
    );
  }, [data]);

  useEffect(() => {
    if (addData?.time) {
      dispatch(getAllStockReturns());
    }
  }, [addData]);

  const getItemsForInvoice = (rowData) => {
    if (rowData) {
      return rowData?.map((value, index) => {
        return {
          id: index,
          title: value?.food?.name,
          foodId: value?.food?.foodId,
          description: value?.code,
          quantity: value?.initialQty,
          storeUnitQty: value?.initialQty,
          price: value?.cost,
        };
      });
    }
  };

  const parseWorkflowLogs = (rowData) => {
    const rawLog = rowData?.workflowLog;
    const parsedLog = rawLog ? safeJSONParse(rawLog) : null;

    // If workflow log entries exist, use them
    if (Array.isArray(parsedLog) && parsedLog.length > 0) {
      return parsedLog.map((log) => ({
        ...log,
        date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
      }));
    }

    // Fallback for old records: build from returnnote data
    const fallbackLogs = [];

    if (rowData?.enteredUser) {
      fallbackLogs.push({
        label: 'Stock Return Created By',
        user: rowData.enteredUser,
        date: rowData?.dateTime ? utcMoment(rowData.dateTime).format('YYYY-MM-DD HH:mm:ss') : '',
      });
    }

    if (rowData?.financeConfirmedUser && rowData?.status === 'FINANCE_CONFIRMED') {
      fallbackLogs.push({
        label: 'Stock Return Finance Confirmed By',
        user: rowData.financeConfirmedUser,
        date: '',
      });
    }

    if (rowData?.financeConfirmedUser && rowData?.status === 'FINANCE_DELETED') {
      fallbackLogs.push({
        label: 'Stock Return Canceled By',
        user: rowData.financeConfirmedUser,
        date: '',
      });
    }

    return fallbackLogs;
  };

  const handleDownload = async (row) => {
    const rowData = row?.original;

    setWorkflowLog(parseWorkflowLogs(rowData));
    setPdfRow({
      invoiceNumber: `Return-${rowData?.returnNoteId}`,
      createDate: utcMoment(row?.original?.date).format('DD MMMM yyyy').toString(),
      dueDate: utcMoment(row?.original?.date).add(7, 'days').format('DD MMMM yyyy').toString(),
      status: rowData?.status,
      discount: 0,
      taxes: 0,
      paidAmount: handleNumber(rowData?.paidAmount),
      totalPrice: handleNumber(rowData?.amount) - handleNumber(rowData?.paidAmount),
      subTotalPrice: handleNumber(rowData?.amount),
      topic: 'Return Note',
      invoiceFrom: {
        name: COMPANY_DATA_FOR_PDF?.name,
        address: selectedLocation?.address,
        phone: selectedLocation?.contactNumber,
      },
      invoiceTo: {
        name: rowData?.supplierInfo?.name,
        address: rowData?.supplierInfo?.address,
        phone: rowData?.supplierInfo?.companyPhone,
      },
      items: getItemsForInvoice(rowData?.stockDetails),
      stockCodes: safeJSONParse(rowData?.stockCodes) || [],
    });
    setIsPdfDrawerOpen(true);
  };

  const columns = !returnsList?.[0]
    ? []
    : Object.keys(returnsList?.[0])
        .map((value) => {
          if (
            value === 'dateTime' ||
            value === 'userId' ||
            value === 'financeConfirmedUserId' ||
            value === 'supplierId' ||
            value === 'supplierInfo' ||
            value === 'userInfo' ||
            value === 'stockDetails' ||
            value === 'workflowLog'
          ) {
            return null;
          } else if (value === 'amount' || value === 'paidAmount') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ row }) => fCurrency(row?.original?.amount),
            };
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        .filter((value) => value);

  return (
    <Page title='View Returns'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='View Returns'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Returns',
              href: PATH_DASHBOARD.stocks.root,
            },
            { name: 'View Returns' },
          ]}
          action={[]}
        />

        {accessVerify('STOCK_RETURN_PAGE') ? (
          <DataGridTable
            name={'View Returns'}
            data={returnsList}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowActions={true}
            renderRowActionItems={(row, closeMenu) => [
              <div style={{ minWidth: '100px' }}>
                <Tooltip title='Download PDF'>
                  <IconButton
                    color='error'
                    onClick={() => {
                      handleDownload(row);
                    }}
                  >
                    <PictureAsPdf />
                  </IconButton>
                </Tooltip>
                {row?.original?.status === 'PENDING' && (
                  <>
                    <Tooltip title='Approve return note'>
                      <IconButton
                        color='success'
                        onClick={() => {
                          dispatch(
                            updateStockReturnStatus({
                              id: row?.original?.returnNoteId,
                              status: 'FINANCE_CONFIRMED',
                              selectedLocationId: selectedLocation?.id,
                            })
                          );
                        }}
                      >
                        <Iconify icon={'line-md:confirm-circle-filled'} width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Cancel return note'>
                      <IconButton
                        color='error'
                        onClick={() => {
                          dispatch(
                            updateStockReturnStatus({
                              id: row?.original?.returnNoteId,
                              status: 'FINANCE_DELETED',
                              selectedLocationId: selectedLocation?.id,
                            })
                          );
                        }}
                      >
                        <Iconify icon={'line-md:close-circle-filled'} width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>,
            ]}
          />
        ) : (
          <PermissionRequired />
        )}

        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} invoice={pdfRow} workflowLog={workflowLog} />
      </Container>
    </Page>
  );
}
